import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock,
  CheckCircle2,
  MinusCircle,
  FileText,
  Upload,
  Loader2,
  Trash2,
  Plus,
  Send,
  FileCheck,
  User,
  Users,
  Briefcase,
  Building2,
} from "lucide-react";
import { mattersApi } from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";

// ── Status cycling ──────────────────────────────────────────────────

const STATUS_CYCLE = ["pending", "received", "not_applicable"] as const;

function cycleStatus(current: string): string {
  const idx = STATUS_CYCLE.indexOf(current as any);
  return STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
}

// ── Source config ───────────────────────────────────────────────────

type SourceFilter = "all" | "client" | "other_side" | "firm";

const SOURCE_TABS: { key: SourceFilter; label: string; Icon: React.FC<any>; color: string; bg: string; bar: string }[] = [
  { key: "all", label: "All", Icon: FileCheck, color: "text-slate-700", bg: "bg-slate-100", bar: "bg-emerald-500" },
  { key: "client", label: "Client", Icon: User, color: "text-blue-700", bg: "bg-blue-100", bar: "bg-blue-500" },
  { key: "other_side", label: "Other Side", Icon: Users, color: "text-amber-700", bg: "bg-amber-100", bar: "bg-amber-500" },
  { key: "firm", label: "Firm", Icon: Briefcase, color: "text-purple-700", bg: "bg-purple-100", bar: "bg-purple-500" },
];

const SOURCE_BADGE: Record<string, { label: string; cls: string }> = {
  client: { label: "Client", cls: "bg-blue-100 text-blue-700" },
  other_side: { label: "Other Side", cls: "bg-amber-100 text-amber-700" },
  firm: { label: "Firm", cls: "bg-purple-100 text-purple-700" },
};

// ── Props ───────────────────────────────────────────────────────────

interface DocumentChecklistPanelProps {
  matterId: string;
  checklist: any[];
  setChecklist: (items: any[]) => void;
  checklistLoading: boolean;
  client: any;
  onShowAddEmailModal: () => void;
}

// ── Component ───────────────────────────────────────────────────────

const DocumentChecklistPanel: React.FC<DocumentChecklistPanelProps> = ({
  matterId,
  checklist,
  setChecklist,
  checklistLoading,
  client,
  onShowAddEmailModal,
}) => {
  const [newChecklistItem, setNewChecklistItem] = useState({ key: "", label: "", required: false });
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [contactingIndex, setContactingIndex] = useState<number | null>(null);
  const [activeSource, setActiveSource] = useState<SourceFilter>("all");

  // Bulk request modal state
  const [showBulkRequestModal, setShowBulkRequestModal] = useState(false);
  const [selectedPendingIndices, setSelectedPendingIndices] = useState<number[]>([]);
  const [bulkSending, setBulkSending] = useState(false);

  // Feedback toast state (replaces alert() calls)
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  React.useEffect(() => {
    if (!feedbackMessage) return;
    const t = setTimeout(() => setFeedbackMessage(null), 4000);
    return () => clearTimeout(t);
  }, [feedbackMessage]);

  // Check if any items have source info (auto-populated RE matters will have it)
  const hasSourceInfo = useMemo(
    () => checklist.some((item: any) => item.source && item.source !== "client"),
    [checklist]
  );

  // Show source tabs only when there are source-tagged items with multiple sources
  const showSourceTabs = useMemo(() => {
    if (checklist.length === 0) return false;
    const sources = new Set(checklist.map((item: any) => item.source || "client"));
    return sources.size > 1;
  }, [checklist]);

  // Filtered checklist based on active source
  const filteredChecklist = useMemo(() => {
    if (activeSource === "all") return checklist.map((item: any, i: number) => ({ ...item, _origIdx: i }));
    return checklist
      .map((item: any, i: number) => ({ ...item, _origIdx: i }))
      .filter((item: any) => (item.source || "client") === activeSource);
  }, [checklist, activeSource]);

  // Per-source stats
  const sourceStats = useMemo(() => {
    const stats: Record<string, { total: number; received: number }> = {
      client: { total: 0, received: 0 },
      other_side: { total: 0, received: 0 },
      firm: { total: 0, received: 0 },
    };
    checklist.forEach((item: any) => {
      const src = item.source || "client";
      if (stats[src]) {
        stats[src].total++;
        if (item.status === "received") stats[src].received++;
      }
    });
    return stats;
  }, [checklist]);

  // Client-only pending items for bulk request
  const clientPendingItems = useMemo(
    () =>
      checklist
        .map((item: any, index: number) => ({ ...item, originalIndex: index }))
        .filter((item: any) => item.status === "pending" && (item.source || "client") === "client"),
    [checklist]
  );

  const allPendingItems = useMemo(
    () =>
      checklist
        .map((item: any, index: number) => ({ ...item, originalIndex: index }))
        .filter((item: any) => item.status === "pending"),
    [checklist]
  );

  // ── Handlers ──────────────────────────────────────────────────────

  const refreshChecklist = async () => {
    const res = await mattersApi.getDocumentChecklist(matterId);
    setChecklist(res.data || []);
  };

  const handleChecklistStatusChange = async (index: number, status: string) => {
    try {
      await mattersApi.updateChecklistItem(matterId, index, { status });
      await refreshChecklist();
    } catch (err) {
      console.error("Failed to update checklist item:", err);
    }
  };

  const handleChecklistUpload = async (index: number, file: File) => {
    setUploadingIndex(index);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await mattersApi.uploadChecklistDocument(matterId, index, formData);
      await refreshChecklist();
    } catch (err) {
      console.error("Failed to upload document:", err);
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.label.trim()) return;
    const key = newChecklistItem.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const updated = [
      ...checklist,
      {
        key: `custom_${key}_${Date.now()}`,
        label: newChecklistItem.label.trim(),
        required: newChecklistItem.required,
        status: "pending",
        documentId: null,
        notes: null,
      },
    ];
    try {
      await mattersApi.updateDocumentChecklist(matterId, updated);
      await refreshChecklist();
      setNewChecklistItem({ key: "", label: "", required: false });
    } catch (err) {
      console.error("Failed to add checklist item:", err);
    }
  };

  const handleRemoveChecklistItem = async (index: number) => {
    const updated = checklist.filter((_: any, i: number) => i !== index);
    try {
      await mattersApi.updateDocumentChecklist(matterId, updated);
      await refreshChecklist();
    } catch (err) {
      console.error("Failed to remove checklist item:", err);
    }
  };

  const handleContactClientForItem = async (index: number, label: string) => {
    const clientEmail =
      client?.emails?.find((e: any) => e.label === "main")?.email ||
      client?.emails?.[0]?.email;
    if (!clientEmail) {
      onShowAddEmailModal();
      return;
    }
    setContactingIndex(index);
    try {
      await mattersApi.contactClientForDocument(matterId, index, { channel: "email" });
      setFeedbackMessage({ type: "success", text: `Request sent to ${clientEmail} for "${label}"` });
    } catch (err: any) {
      setFeedbackMessage({ type: "error", text: err?.response?.data?.error || "Failed to send request" });
    } finally {
      setContactingIndex(null);
    }
  };

  const handleBulkRequest = async () => {
    const clientEmail =
      client?.emails?.find((e: any) => e.label === "main")?.email ||
      client?.emails?.[0]?.email;
    if (!clientEmail) {
      setShowBulkRequestModal(false);
      onShowAddEmailModal();
      return;
    }
    setBulkSending(true);
    try {
      await mattersApi.sendDocumentReminder(matterId, selectedPendingIndices);
      setFeedbackMessage({ type: "success", text: `Document request sent to ${clientEmail} for ${selectedPendingIndices.length} item${selectedPendingIndices.length !== 1 ? "s" : ""}` });
      setShowBulkRequestModal(false);
    } catch (err: any) {
      setFeedbackMessage({ type: "error", text: err?.response?.data?.error || "Failed to send request" });
    } finally {
      setBulkSending(false);
    }
  };

  // ── Drag handlers ─────────────────────────────────────────────────

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes("Files")) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
    const file = e.dataTransfer.files?.[0];
    if (file) handleChecklistUpload(index, file);
  };

  // ── Render ────────────────────────────────────────────────────────

  if (checklistLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold text-slate-800">Document Checklist</h3>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const receivedCount = checklist.filter((c: any) => c.status === "received").length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-800">Document Checklist</h3>
        {clientPendingItems.length > 0 && (
          <button
            onClick={() => {
              setSelectedPendingIndices(clientPendingItems.map((p) => p.originalIndex));
              setShowBulkRequestModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            <Send className="h-4 w-4" />
            Request from Client ({clientPendingItems.length})
          </button>
        )}
      </div>

      {/* Feedback toast */}
      <AnimatePresence>
        {feedbackMessage && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
              feedbackMessage.type === "success"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {feedbackMessage.type === "success" ? (
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            ) : (
              <MinusCircle className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="flex-1">{feedbackMessage.text}</span>
            <button onClick={() => setFeedbackMessage(null)} className="text-current opacity-60 hover:opacity-100">
              <span className="sr-only">Dismiss</span>&times;
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* No email warning */}
      {client && !client.emails?.find((e: any) => e.label === "main")?.email && !client.emails?.[0]?.email && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <span>Client has no email address on file. Add an email to send document requests.</span>
          <button
            onClick={onShowAddEmailModal}
            className="ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-100 hover:bg-amber-200 transition-colors whitespace-nowrap"
          >
            Add Email
          </button>
        </div>
      )}

      {/* Source filter tabs */}
      {showSourceTabs && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-1.5">
          <div className="flex gap-1">
            {SOURCE_TABS.map((tab) => {
              const count =
                tab.key === "all"
                  ? checklist.length
                  : sourceStats[tab.key]?.total || 0;
              if (tab.key !== "all" && count === 0) return null;
              const isActive = activeSource === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveSource(tab.key)}
                  className="relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors z-10"
                  style={{ color: isActive ? undefined : undefined }}
                >
                  {isActive && (
                    <motion.div
                      layoutId="source-tab-bg"
                      className={`absolute inset-0 rounded-lg ${tab.bg}`}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                  <tab.Icon className={`h-3.5 w-3.5 relative z-10 ${isActive ? tab.color : "text-slate-400"}`} />
                  <span className={`relative z-10 ${isActive ? tab.color : "text-slate-500"}`}>
                    {tab.label}
                  </span>
                  <span
                    className={`relative z-10 text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                      isActive ? `${tab.bg} ${tab.color}` : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-source progress summary */}
      {showSourceTabs ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-3">
          {(["client", "other_side", "firm"] as const).map((src) => {
            const stats = sourceStats[src];
            if (!stats || stats.total === 0) return null;
            const tab = SOURCE_TABS.find((t) => t.key === src)!;
            const pct = stats.total > 0 ? (stats.received / stats.total) * 100 : 0;
            return (
              <div key={src} className="flex items-center gap-3">
                <span className={`text-xs font-medium w-20 ${tab.color}`}>{tab.label}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                  <motion.div
                    className={`${tab.bar} h-1.5 rounded-full`}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  />
                </div>
                <span className="text-xs text-slate-500 w-16 text-right tabular-nums">
                  {stats.received}/{stats.total}
                </span>
              </div>
            );
          })}
          <div className="border-t border-slate-100 pt-2 flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-700 w-20">Overall</span>
            <div className="flex-1 bg-slate-100 rounded-full h-2">
              <motion.div
                className="bg-emerald-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${checklist.length > 0 ? (receivedCount / checklist.length) * 100 : 0}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-700 w-16 text-right tabular-nums">
              {receivedCount}/{checklist.length}
            </span>
          </div>
        </div>
      ) : (
        /* Simple progress bar when no source info */
        checklist.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-slate-700">
                {receivedCount} of {checklist.length} documents received
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-emerald-500 h-2 rounded-full transition-all"
                style={{ width: `${(receivedCount / checklist.length) * 100}%` }}
              />
            </div>
          </div>
        )
      )}

      {/* Checklist items */}
      {filteredChecklist.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10 text-center text-slate-500">
          <FileCheck className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          {activeSource !== "all" ? (
            <>
              <p>No {SOURCE_BADGE[activeSource]?.label || activeSource} documents</p>
              <p className="text-xs mt-1">Switch to "All" to see all checklist items</p>
            </>
          ) : (
            <>
              <p>No document checklist items</p>
              <p className="text-xs mt-1">Add items below to track required documents</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence mode="popLayout">
            {filteredChecklist.map((item: any) => {
              const origIdx = item._origIdx;
              const badge = SOURCE_BADGE[item.source];
              return (
                <motion.div
                  key={item.key || origIdx}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                  className={`bg-white rounded-xl shadow-sm border p-4 flex items-center gap-3 transition-colors ${
                    dragOverIndex === origIdx
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-slate-200"
                  }`}
                  onDragOver={(e) => handleDragOver(e, origIdx)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, origIdx)}
                >
                  {/* Clickable status bubble */}
                  <button
                    onClick={() => handleChecklistStatusChange(origIdx, cycleStatus(item.status))}
                    className="flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                    title={`Click to change status (${item.status})`}
                  >
                    {item.status === "received" ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    ) : item.status === "not_applicable" ? (
                      <MinusCircle className="h-5 w-5 text-slate-400" />
                    ) : (
                      <Clock className="h-5 w-5 text-amber-400" />
                    )}
                  </button>

                  {/* Label, required badge, source badge, doc link, upload tracking */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-slate-800 text-sm">{item.label}</p>
                      {item.required && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-50 text-red-600">
                          Required
                        </span>
                      )}
                      {badge && showSourceTabs && activeSource === "all" && (
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${badge.cls}`}>
                          {badge.label}
                        </span>
                      )}
                    </div>
                    {item.documentId && (
                      <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {typeof item.documentId === "object"
                          ? item.documentId.originalFilename || item.documentId.title || "Document uploaded"
                          : "Document uploaded"}
                      </p>
                    )}
                    {item.uploadedAt && (
                      <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                        {item.uploadedByClient ? (
                          <>
                            <User className="h-3 w-3 text-blue-400" />
                            <span className="text-blue-500 font-medium">Client uploaded</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-3 w-3" />
                            <span>
                              Uploaded by{" "}
                              {typeof item.uploadedBy === "object"
                                ? item.uploadedBy?.displayName || "staff"
                                : "staff"}
                            </span>
                          </>
                        )}
                        <span>
                          on {new Date(item.uploadedAt).toLocaleDateString()} at{" "}
                          {new Date(item.uploadedAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Status dropdown */}
                  <div className="flex-shrink-0 w-32">
                    <SearchableSelect
                      options={[
                        { value: "pending", label: "Pending" },
                        { value: "received", label: "Received" },
                        { value: "not_applicable", label: "N/A" },
                      ]}
                      value={item.status}
                      onChange={(val) => {
                        if (val) handleChecklistStatusChange(origIdx, val);
                      }}
                      placeholder="Status"
                    />
                  </div>

                  {/* Upload area with drag & drop text */}
                  <label className="flex-shrink-0 cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleChecklistUpload(origIdx, f);
                      }}
                    />
                    <div
                      className={`px-3 py-2 rounded-lg border border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50 transition-colors flex items-center gap-1.5 ${
                        uploadingIndex === origIdx ? "opacity-50" : ""
                      }`}
                    >
                      {uploadingIndex === origIdx ? (
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      ) : (
                        <>
                          <Upload className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            Upload or drag & drop
                          </span>
                        </>
                      )}
                    </div>
                  </label>

                  {/* Contact client button — only for client-source items */}
                  {(item.source || "client") === "client" && (
                    <button
                      onClick={() => handleContactClientForItem(origIdx, item.label)}
                      disabled={contactingIndex === origIdx}
                      className="flex-shrink-0 p-2 rounded-lg border border-slate-200 hover:bg-blue-50 hover:border-blue-300 text-slate-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                      title={`Ask client to upload "${item.label}"`}
                    >
                      {contactingIndex === origIdx ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </button>
                  )}

                  {/* Delete button */}
                  <button
                    onClick={() => handleRemoveChecklistItem(origIdx)}
                    className="flex-shrink-0 p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Add new checklist item */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={newChecklistItem.label}
            onChange={(e) => setNewChecklistItem({ ...newChecklistItem, label: e.target.value })}
            placeholder="New document name..."
            className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddChecklistItem();
            }}
          />
          <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={newChecklistItem.required}
              onChange={(e) => setNewChecklistItem({ ...newChecklistItem, required: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600"
            />
            Required
          </label>
          <button
            onClick={handleAddChecklistItem}
            disabled={!newChecklistItem.label.trim()}
            className="px-4 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Bulk request modal — only client-source pending items */}
      <PremiumModal
        open={showBulkRequestModal}
        onClose={() => setShowBulkRequestModal(false)}
        title="Request Documents from Client"
        subtitle="Select which documents to include in the request email"
        icon={Send}
        size="md"
        footer={
          <div className="flex justify-end gap-3 w-full">
            <button
              onClick={() => setShowBulkRequestModal(false)}
              className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkRequest}
              disabled={bulkSending || selectedPendingIndices.length === 0}
              className="px-4 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {bulkSending
                ? "Sending..."
                : `Send Request (${selectedPendingIndices.length} item${selectedPendingIndices.length !== 1 ? "s" : ""})`}
            </button>
          </div>
        }
      >
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-slate-600 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedPendingIndices.length === clientPendingItems.length && clientPendingItems.length > 0}
              onChange={(e) =>
                setSelectedPendingIndices(
                  e.target.checked ? clientPendingItems.map((p) => p.originalIndex) : []
                )
              }
              className="h-4 w-4 rounded border-slate-300 text-emerald-600"
            />
            Select All
          </label>
          {clientPendingItems.map((item) => (
            <label
              key={item.key}
              className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedPendingIndices.includes(item.originalIndex)}
                onChange={(e) => {
                  setSelectedPendingIndices((prev) =>
                    e.target.checked
                      ? [...prev, item.originalIndex]
                      : prev.filter((i) => i !== item.originalIndex)
                  );
                }}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600"
              />
              <span className="text-sm text-slate-800">{item.label}</span>
              {item.required && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-50 text-red-600">
                  Required
                </span>
              )}
            </label>
          ))}
        </div>
      </PremiumModal>
    </div>
  );
};

export default DocumentChecklistPanel;
